import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { JobEditorBaseRouting } from '@apps/job-editor';
import { useFeaturesStore } from '@shared/stores/features.store';
import { installAdminSessionGuard } from '@shared/adminSessionGuard';
import { CustomerPackageBuilderRouting } from '@apps/customer-package-builder';
import { CustomerCartRoutes } from '@apps/cart';
import { CheckoutRoutes } from '@apps/checkout';
import { AdminRouting } from './apps/admin';
import { CustomerRouting, DigitalRoute } from './apps/customer';
import { StudioAdminRouting } from './apps/studioadmin';
import { AccountRouting } from './apps/account';
import { SignupRouting } from './apps/signup';
import { HomeRouting } from './apps/home';

const CUSTOMER_AREA: RouteRecordRaw = {
  path: '/:studioSlug',
  component: () => import('./apps/customer/layouts/CustomerLayout.vue'),
  children: [...CustomerRouting, ...CustomerPackageBuilderRouting, ...CustomerCartRoutes, ...CheckoutRoutes],
};

const ACCOUNT_AREA: RouteRecordRaw = {
  path: '/account',
  component: () => import('./apps/account/layouts/AccountLayout.vue'),
  children: [...AccountRouting],
};

const SIGNUP_AREA: RouteRecordRaw = {
  path: '/signup',
  component: () => import('./apps/account/layouts/AccountLayout.vue'),
  children: [...SignupRouting],
};

const HOME_AREA: RouteRecordRaw = {
  path: '/',
  component: () => import('./apps/home/layouts/HomeLayout.vue'),
  children: [...HomeRouting, ...DigitalRoute],
};

const MAINTENANCE_AREA: RouteRecordRaw = {
  path: '/maintenance',
  component: () => import('./shared/pages/MaintenancePage.vue'),
};

const routes: RouteRecordRaw[] = [
  HOME_AREA,
  ...AdminRouting,
  ...StudioAdminRouting,
  ...JobEditorBaseRouting,

  CUSTOMER_AREA,
  ACCOUNT_AREA,
  SIGNUP_AREA,
  MAINTENANCE_AREA,
];

const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return { top: 0 };
  },
});

const setDocumentTitle = (route: RouteLocationNormalized) => {
  const suffix = 'My Pro Images';
  const title = route.meta.title;
  document.title = title ? `${title} | ${suffix}` : suffix;
};

router.beforeEach((to) => {
  setDocumentTitle(to);
});

installAdminSessionGuard(router);

const environment = import.meta.env.MODE; // 'development', 'staging', 'production'

function normalizeContentGroupPath(path: string): string {
  if (path === '/' || path === '') return path;

  const segments = path.split('/').filter(Boolean);

  if (segments.length === 0) return '/';

  const rootLevelSegments = new Set([
    'account',
    'maintenance',
    'studio',
    'photos',
    'gallery',
    'order',
    'cart',
    'checkout',
  ]);

  if (rootLevelSegments.has(segments[0])) {
    return `/${segments.join('/')}`;
  }

  if (segments.length >= 3) {
    return `/${segments.slice(2).join('/')}`;
  }

  if (segments.length >= 2) {
    return `/${segments.slice(1).join('/')}`;
  }

  return '/';
}

function getContentGroup(path: string): string {
  const normalizedPath = normalizeContentGroupPath(path);

  if (normalizedPath.startsWith('/account/login') || normalizedPath.startsWith('/account/register')) return 'Authentication';
  if (normalizedPath.startsWith('/account')) return 'Account Management';
  if (normalizedPath.startsWith('/cart') || normalizedPath.startsWith('/checkout')) return 'Cart & Checkout';
  if (normalizedPath.startsWith('/studio/') || normalizedPath.match(/\/[\w-]+-photography/)) return 'Photographer Storefront';
  if (normalizedPath.startsWith('/photos/') || normalizedPath.startsWith('/gallery/')) return 'Photo Selection';
  if (normalizedPath.startsWith('/order')) return 'Order Management';
  if (normalizedPath === '/' || normalizedPath === '') return 'Homepage';
  return 'Other';
}

router.afterEach(async (to, from) => {
  // Get features store to check if Marker.io is enabled
  const featuresStore = useFeaturesStore();

  // Initialize features store if not already initialized
  if (!featuresStore.initialized) {
    await featuresStore.initialize();
  }

  const markerioEnabled = featuresStore.initialized ? featuresStore.hasFeature('markerio') : false;

  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'virtual_pageview',
    page_path: to.path,
    page_title: to.meta.title || document.title,
    page_location: window.location.origin + window.location.pathname,
    previous_path: from.path || '(direct)',
    environment: environment,
    markerio_enabled: markerioEnabled,
    content_group: getContentGroup(to.path),
    studio_name: (to.params.studioSlug as string) || 'none',
  });
});

export { router };

declare module 'vue-router' {
  interface RouteMeta {
    breadCrumb?: {
      label: string;
      icon?: IconDefinition;
    };
  }
}
